/**
 * How the lead was last reached, as one quiet metadata row.
 *
 * Only the channel. The lane used to sit here too, but it tells an agent
 * nothing the signal chips do not already carry — a live transfer shows up
 * there as "live transfer connected" or "live transfer missed", and the default
 * lane means only that no transfer was attempted. What is left is the part that
 * changes the next move: was this lead last on a call, or on a text.
 *
 * Deliberately not a pill. These surfaces already carry a hotness pill, a status
 * pill and a row of signal chips; another badge would add noise without adding
 * emphasis. It reads like the timestamp it sits beside, with a small icon
 * carrying the meaning instead of a border.
 *
 * Location is deliberately NOT here — it belongs next to the contact name (see
 * HandoffLocationLabel below), where it reads as part of who this lead is.
 *
 * Renders nothing when the row predates the field.
 */

import React from 'react';
import { MapPin, MessageSquare, Mic } from 'lucide-react';
import { ChannelOfOrigin, HandoffLocation } from '../../../types/handoff.types';
import { formatHandoffLocation } from '../../../utils/handoffLane';
import { cn } from '../../../utils/cn';

interface HandoffMetaLineProps {
  channel?: ChannelOfOrigin | string | null;
  className?: string;
}

const ICON = 'h-4 w-4 shrink-0 opacity-80';

/** Compact inline form — "Voice-generated" is badge wording, too heavy here. */
const inlineChannel = (channel: ChannelOfOrigin | string | null | undefined): string | null => {
  if (channel === 'text') return 'via sms';
  if (channel === 'voice') return 'via call';
  return null;
};

export const HandoffMetaLine: React.FC<HandoffMetaLineProps> = ({ channel, className }) => {
  const channelText = inlineChannel(channel);
  if (!channelText) return null;

  return (
    <span
      className={cn(
        'inline-flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] leading-5 text-foreground/70',
        className
      )}
    >
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
        Last engaged
      </span>
      <span className="inline-flex items-center gap-1.5">
        {channel === 'text' ? <MessageSquare className={ICON} /> : <Mic className={ICON} />}
        {channelText}
      </span>
    </span>
  );
};

/**
 * The lead's city / state, shown beside their name. Same icon treatment as the
 * metadata row so the two read as one family.
 */
export const HandoffLocationLabel: React.FC<{
  location?: HandoffLocation | null;
  className?: string;
}> = ({ location, className }) => {
  const text = formatHandoffLocation(location);
  if (!text) return null;

  return (
    <span
      className={cn(
        // Colour, size and weight are all inherited from the row it sits in —
        // it shares that row with the phone number and has to match it, icon
        // included. Only the glyph differs.
        'inline-flex items-center gap-1 whitespace-nowrap',
        className
      )}
    >
      <MapPin className="h-3.5 w-3.5 shrink-0" />
      {text}
    </span>
  );
};

export default HandoffMetaLine;
